'use client';

import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import { ConfirmationDialog, ListPageLayout } from '@terra/ui-design-system';
import type { ReportsReturnDto } from '@terra/shared-contracts-reports';
import { useAppTargetId } from '../../store/projectStore';
import { useReports } from '../../hooks/useReports';
import { useTabScopedReportsFilters } from '../../store/tab/reportsTabScopedFilterStore';
import { ReportDetailsDrawer } from './ReportDetailsDrawer';
import { ReportsContextNavigation } from './reports-header/ReportsContextNavigation';
import { ReportsFilterPanel } from './reports-header/ReportsFilterPanel';
import {
  REPORTS_TABLE_EMPTY_KIND,
  ReportsTable,
  type ReportsTableEmptyKind,
} from './reports-table/ReportsTable';
import {
  countReportsBySourceChip,
  filterReportsBeforeSource,
  filterReportsBySource,
  hasActiveReportListFilters,
  hasAnyReportFilters,
  sortReportsNewestFirst,
} from './reports-table/reports-list.model';
import { useReportDelete } from './reports-table/useReportDelete';

type ReportsContentProps = {
  header?: ReactNode;
};

export function ReportsContent({ header }: ReportsContentProps) {
  const t = useTranslations('proxy.reports.delete');
  const tCommon = useTranslations('designSystem.common');
  const appTargetId = useAppTargetId();
  const { reports, isLoading, isFetching, refetch, deleteReport } = useReports({
    applicationId: appTargetId,
  });
  const filters = useTabScopedReportsFilters();
  const [viewingReport, setViewingReport] = useState<ReportsReturnDto | null>(
    null,
  );
  const filterValues = useMemo(
    () => ({
      searchTerm: filters.searchTerm,
      sourceChip: filters.sourceChip,
      createdStartDate: filters.createdStartDate,
      createdEndDate: filters.createdEndDate,
    }),
    [
      filters.createdEndDate,
      filters.createdStartDate,
      filters.searchTerm,
      filters.sourceChip,
    ],
  );
  const reportsBeforeSource = useMemo(
    () => filterReportsBeforeSource({ reports, filters: filterValues }),
    [filterValues, reports],
  );
  const sourceCounts = useMemo(
    () => countReportsBySourceChip(reportsBeforeSource),
    [reportsBeforeSource],
  );
  const visibleReports = useMemo(
    () =>
      sortReportsNewestFirst(
        filterReportsBySource({
          reports: reportsBeforeSource,
          sourceChip: filterValues.sourceChip,
        }),
      ),
    [filterValues.sourceChip, reportsBeforeSource],
  );
  const hasFilters = hasAnyReportFilters(filterValues);
  const emptyKind: ReportsTableEmptyKind = !appTargetId
    ? REPORTS_TABLE_EMPTY_KIND.NO_TARGET
    : hasFilters
      ? REPORTS_TABLE_EMPTY_KIND.FILTERED
      : REPORTS_TABLE_EMPTY_KIND.EMPTY;
  const handleDeleted = useCallback(
    (report: ReportsReturnDto) => {
      setViewingReport((current) =>
        current?.id === report.id ? null : current,
      );
      void refetch();
    },
    [refetch],
  );
  const reportDelete = useReportDelete({
    deleteReport,
    onDeleted: handleDeleted,
  });

  return (
    <ListPageLayout
      header={header}
      navigation={
        <ReportsContextNavigation
          sourceChip={filters.sourceChip}
          sourceCounts={isLoading ? null : sourceCounts}
          onSourceChipChange={filters.setSourceChip}
          searchTerm={filters.searchTerm}
          onSearchTermChange={filters.setSearchTerm}
          onRefresh={() => void refetch()}
          isLoading={isFetching}
        />
      }
      filters={
        <ReportsFilterPanel
          createdStartDate={filters.createdStartDate}
          createdEndDate={filters.createdEndDate}
          onCreatedStartDateChange={filters.setCreatedStartDate}
          onCreatedEndDateChange={filters.setCreatedEndDate}
          hasActiveFilters={hasActiveReportListFilters(filterValues)}
          onClear={filters.clearListFilters}
          showFilteredCounter={hasFilters}
          filteredCount={visibleReports.length}
          totalCount={reports.length}
        />
      }
    >
      <ReportsTable
        reports={visibleReports}
        selectedReportId={viewingReport?.id ?? null}
        isLoading={isLoading}
        emptyKind={emptyKind}
        onOpenReport={setViewingReport}
        onDeleteReport={reportDelete.request}
      />

      <ConfirmationDialog
        isOpen={reportDelete.isDialogOpen}
        onOpenChange={({ open }) => {
          if (!open) {
            reportDelete.cancel();
          }
        }}
        onConfirm={() => void reportDelete.confirm()}
        loading={reportDelete.isDeleting}
        variant="destructive"
        title={t('dialogTitle')}
        description={t('dialogDescription', {
          title: reportDelete.reportToDelete?.title ?? '',
        })}
        confirmText={tCommon('delete')}
        cancelText={tCommon('cancel')}
      />

      <ReportDetailsDrawer
        report={viewingReport}
        isOpen={viewingReport !== null}
        onClose={() => setViewingReport(null)}
      />
    </ListPageLayout>
  );
}
